import { getHistory, setHistory } from '~/hooks/use-screens-history';
import { type FeedEntry } from '~/services/layout/index.ts';
import {
  ANALYTICS_PROPERTIES_KEYS,
  PLUGIN_CONFIG_ANALYTICS_PROPERTIES_KEYS,
} from '../consts';
import type { MuxAnalyticsProps } from '~/services/analytics/events-names';
import logger from '~/services/logger';
import type Player from 'video.js/dist/types/player';
import { getTranslations } from '~/services/localization/index.ts';
import { WAIT_FOR_STOPPED_EVENT_BEFORE_NAVIGATION } from '../plugins/continue-watching';

export const getBoolValue = (value: any): boolean => {
  if (typeof value === 'boolean') return value;
  const preparedValue = typeof value === 'string' ? value.toLowerCase() : value;

  return (
    preparedValue === 'true' || preparedValue === '1' || preparedValue === 1
  );
};

const isLiveByDuration = (duration: string): boolean => {
  const durationNumber = duration && parseFloat(duration);

  if (!durationNumber) return false;

  return durationNumber <= 0;
};

const hasLiveExtension = (entry: any): boolean => {
  return !!(
    entry.type?.value === 'channel' ||
    getBoolValue(entry.extensions?.live) ||
    getBoolValue(entry.extensions?.isLive)
  );
};

export const isLive = (entry: any): boolean => {
  if (!entry) return false;

  const duration = entry.extensions?.duration;

  return hasLiveExtension(entry) || isLiveByDuration(duration);
};

export function getVideoItemParentUrl(entry: FeedEntry): string {
  const {
    id,
    type,
  }: {
    id: string;
    type: string;
  } = entry?.extensions?.parent || {};

  const limitedHistory = getHistory();
  const lastScreen = limitedHistory.pop();
  setHistory(limitedHistory);

  if (!id || !type) return lastScreen ? lastScreen : '/';

  return `/_content-type/${type}?_id=${encodeURIComponent(id)}`;
}

export function parseTimeToSeconds(time: string): number {
  // Split the time string by colon
  const parts = time.split(':').map((part) => parseInt(part, 10));

  let seconds = 0;

  // Calculate the seconds based on the number of parts
  if (parts.length === 2) {
    // Format is MM:SS
    seconds = parts[0] * 60 + parts[1];
  } else if (parts.length === 3) {
    // Format is HH:MM:SS
    seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else {
    throw new Error('Invalid time format');
  }

  return seconds;
}

export const findAnalyticsValueByKey = (data: any, configAnalyticsKey: any) => {
  if (!configAnalyticsKey) return null;

  const keys = configAnalyticsKey.split('.');

  // If there's only one key, directly return the value
  if (keys.length === 1 && data.hasOwnProperty(configAnalyticsKey)) {
    return data[configAnalyticsKey];
  }

  let currentData = data;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];

    if (
      !currentData.hasOwnProperty(key) ||
      typeof currentData[key] !== 'object'
    ) {
      break; // Exit the loop if currentMap or its value is not an object
    }

    currentData = currentData[key];
  }

  // Retrieve the last key
  const lastKey = keys[keys.length - 1];

  if (currentData.hasOwnProperty(lastKey)) return currentData[lastKey];
};

export const getAnalyticVideoProperties = (
  config: MuxAnalyticsProps,
  entry: FeedEntry
) => {
  const result: any = {};
  const keys = Object.keys(PLUGIN_CONFIG_ANALYTICS_PROPERTIES_KEYS);

  for (let i = 0; i < keys.length; i++) {
    const pluginConfigAnalyticsKey: keyof MuxAnalyticsProps =
      PLUGIN_CONFIG_ANALYTICS_PROPERTIES_KEYS[keys[i]];

    const analyticsKeyForOptions = ANALYTICS_PROPERTIES_KEYS[keys[i]];
    const analyticsValuePath: string | boolean =
      config[pluginConfigAnalyticsKey];
    const value = findAnalyticsValueByKey(entry, analyticsValuePath);

    if (value) result[analyticsKeyForOptions] = value;
  }

  if (Object.keys(result).length) {
    logger.info(`Set analytics custom properties: ${result}`);
  }

  return result;
};

export const getMuxVideoAnalyticsParams = (
  config: MuxAnalyticsProps,
  entry: FeedEntry
) => {
  const videoProperties = getAnalyticVideoProperties(config, entry);

  return {
    video_id: entry?.id,
    video_title: entry?.title,
    video_stream_type: entry?.type,
    ...videoProperties,
  };
};

export const PERSISTED_PLAYER_SETTINGS_KEY = 'player-settings';

export interface PersistedPlayerSettings {
  volume?: number;
  muted?: boolean;
  captionsEnabled?: boolean;
  selectedTrack?: {
    language?: string;
    label?: string;
  };
}

export const setPersistedPlayerSettings = (
  volume?: number,
  muted?: boolean,
  captionsEnabled?: boolean,
  selectedTrack?: {
    language?: string;
    label?: string;
  }
) => {
  try {
    const data = JSON.stringify({
      volume: Math.round((volume ?? 1) * 100) / 100, // store volume with only 2 decimal places
      muted,
      captionsEnabled,
      selectedTrack,
    });

    sessionStorage.setItem(PERSISTED_PLAYER_SETTINGS_KEY, data);
  } catch (err) {
    console.error('failed to persist player settings');
  }
};

export const getPersistedPlayerSettings =
  (): null | PersistedPlayerSettings => {
    const data = sessionStorage.getItem(PERSISTED_PLAYER_SETTINGS_KEY);

    if (!data) {
      return null;
    }

    try {
      return JSON.parse(data);
    } catch (err) {
      console.error('failed to parse player settings');

      return null;
    }
  };

export function getIsLastNumOfSeconds(
  player: Player,
  lastNumOfSeconds: number = 2
) {
  // @ts-ignore
  return player.duration() - player.currentTime() < lastNumOfSeconds;
}

export function playerGoBack({
  baseURL,
  playableItem,
  layoutIdOverride,
}: {
  baseURL: string;
  playableItem: FeedEntry;
  layoutIdOverride?: string | null;
}) {
  const parentUrl: string = getVideoItemParentUrl(playableItem);
  if (!parentUrl) {
    logger.info(`No parent URL found for the video item: ${playableItem?.id}`);
    return;
  }
  setTimeout(() => {
    const url = new URL(parentUrl, baseURL);

    if (layoutIdOverride) {
      url.searchParams.set('layoutId', layoutIdOverride);
    }

    window.location.href = url.toString();
  });
}

/**
 * Disposes the video.js player and navigates via a given function
 * to ensure CW events are handled before navigating.
 * If `isContinueWatchingSyncedFeed` is true, waits for the
 * 'continue-watching:ready-for-dispose-and-navigation' event
 * or times out after `WAIT_FOR_STOPPED_EVENT_BEFORE_NAVIGATION`.
 * @param player - The video.js player instance.
 * @param isContinueWatchingSyncedFeed - Flag indicating if the continue watching is enabled.
 * @param navigationFunction - The function to call for navigation after disposing the player.
 * @returns void
 */
export function disposePlayerAndNavigate({
  player,
  isContinueWatchingSyncedFeed,
  navigationFunction,
}: {
  player: Player;
  isContinueWatchingSyncedFeed: boolean;
  navigationFunction: () => void;
}): void {
  if (!isContinueWatchingSyncedFeed) {
    player.dispose();
    navigationFunction();
    return;
  }

  player.trigger('continue-watching:send-stopped-event');

  let isStoppedEventFired = false;

  player.on('continue-watching:ready-for-dispose-and-navigation', () => {
    isStoppedEventFired = true;
  });

  const start = performance.now();

  const waitUntilReady = () => {
    if (
      isStoppedEventFired ||
      performance.now() - start >= WAIT_FOR_STOPPED_EVENT_BEFORE_NAVIGATION
    ) {
      navigationFunction();
      return;
    }

    requestAnimationFrame(waitUntilReady);
  };

  waitUntilReady();
}

export const DEFAULT_MANIFEST_BLOCKED_ERROR_MESSAGE =
  'This content is not available in your region.';

export interface ManifestErrorHandlingSetup {
  onVhsXhrResponse: (request: any, _xhrError: unknown, response: any) => void;
  teardown: () => void;
}

export function setupManifestErrorHandling(
  player: Player,
  blockedRef: { current: boolean },
  localizations: Record<string, Record<string, string>> | undefined,
  language: string
): ManifestErrorHandlingSetup {
  const originalPlayerError = player.error;

  const patchedPlayerError = function (err?: unknown) {
    if (arguments.length === 0) return originalPlayerError.call(player);
    const code =
      typeof err === 'number' ? err : Number((err as any)?.code ?? NaN);
    if (blockedRef.current && code === 4) {
      const message =
        getTranslations(localizations, language)?.geo_blocked_error ??
        DEFAULT_MANIFEST_BLOCKED_ERROR_MESSAGE;
      return originalPlayerError.call(player, { code: 4, message } as never);
    }
    return originalPlayerError.call(player, err as never);
  } as typeof player.error;

  player.error = patchedPlayerError;

  const onVhsXhrResponse = (
    request: any,
    _xhrError: unknown,
    response: any
  ): void => {
    const responseType = request?.responseType;
    const canReadResponseText =
      responseType == null || responseType === '' || responseType === 'text';

    let text = '';

    if (canReadResponseText) {
      try {
        text =
          typeof request?.responseText === 'string' ? request.responseText : '';
      } catch {
        text = '';
      }
    }

    const status = response?.statusCode ?? request?.status;
    const isManifest =
      /\.m3u8(\?|#|$)/i.test(request?.uri ?? '') || text.includes('#EXTM3U');
    const isBlocked =
      status === 401 || status === 403 || /geo|location/i.test(text); // Indicates geo-blocked content.
    if (isManifest && isBlocked) blockedRef.current = true;
  };

  const teardown = (): void => {
    if (player.error === patchedPlayerError) {
      player.error = originalPlayerError;
    }
  };

  return {
    onVhsXhrResponse,
    teardown,
  };
}
